import {
    LayoutDashboard,
    Calculator,
    Boxes,
    Receipt,
    Globe,
    Smartphone,
    Cloud,
    Sparkles,
    Wrench,
    Plug,
    Palette,
    Server,
    Bot,
    Cog,
} from "lucide-react";

export const SERVICES = [
    {
        icon: LayoutDashboard,
        title: "ERP Development",
        desc: "Custom enterprise resource planning systems tailored to how your business actually runs.",
        details:
            "Modular ERP with finance, HR, sales, procurement and reporting — built on a scalable, multi-tenant architecture.",
    },
    {
        icon: Calculator,
        title: "Accounting Software",
        desc: "Modern, GST-compliant accounting platforms with audit-ready ledgers and dashboards.",
        details:
            "Double-entry ledgers, automated reconciliation, tax modules and rich financial dashboards.",
    },
    {
        icon: Boxes,
        title: "Inventory Management",
        desc: "Real-time stock visibility across warehouses, branches and channels.",
        details:
            "Batch tracking, barcode/QR support, low-stock alerts and supplier workflows.",
    },
    {
        icon: Receipt,
        title: "Billing & POS",
        desc: "Fast point-of-sale and billing apps for retail, restaurants and services.",
        details:
            "Offline-first POS, thermal printer support, GST invoicing and loyalty programs.",
    },
    {
        icon: Globe,
        title: "Web Development",
        desc: "Production-grade web applications with delightful, performant UIs.",
        details:
            "React, Next.js, TypeScript — SEO-ready, accessible and fast by default.",
    },
    {
        icon: Smartphone,
        title: "Mobile App Development",
        desc: "Cross-platform iOS and Android apps that feel native.",
        details:
            "Flutter and React Native apps with offline sync, push notifications and analytics.",
    },
    {
        icon: Cloud,
        title: "Cloud Solutions",
        desc: "Cloud-native architectures on AWS and Azure with auto-scaling and observability.",
        details:
            "VPC design, IaC with Terraform, CI/CD, monitoring and cost governance.",
    },
    {
        icon: Sparkles,
        title: "SaaS Platforms",
        desc: "Multi-tenant SaaS engineering — from MVP to revenue-ready product.",
        details:
            "Subscription billing, tenant isolation, RBAC, audit logs and white-label theming.",
    },
    {
        icon: Wrench,
        title: "Legacy Modernization",
        desc: "Move from monoliths and spreadsheets to clean, future-proof systems.",
        details:
            "Incremental rewrites, data migrations and strangler-fig patterns with zero downtime.",
    },
    {
        icon: Plug,
        title: "API Development",
        desc: "Robust REST and GraphQL APIs that scale gracefully under load.",
        details:
            "OpenAPI specs, versioning, rate-limiting and developer-friendly docs.",
    },
    {
        icon: Palette,
        title: "UI/UX Revamp",
        desc: "Reimagine tired interfaces into modern, conversion-friendly experiences.",
        details:
            "Design systems, accessibility audits, prototypes and high-fidelity components.",
    },
    {
        icon: Server,
        title: "Hosting & DevOps",
        desc: "Reliable hosting, deployments and observability for production workloads.",
        details:
            "Kubernetes, Docker, GitHub Actions, log aggregation and uptime monitoring.",
    },
    {
        icon: Cog,
        title: "Business Automation",
        desc: "Automate repetitive workflows across sales, finance, support and ops.",
        details:
            "Workflow engines, integrations and document automation that save hours every week.",
    },
    {
        icon: Bot,
        title: "AI-Assisted Workflows",
        desc: "Embed AI into your business — from copilots to intelligent document processing.",
        details:
            "LLM integrations, retrieval-augmented search and AI assistants tuned to your data.",
    },
];

export const PROJECTS = [
    {
        title: "Retail ERP Platform",
        stack: ["React", "FastAPI", "PostgreSQL", "Docker"],
        summary:
            "End-to-end ERP for a multi-store retail chain with inventory, billing and analytics.",
        outcome: "38% faster month-end close",
        image: "https://images.unsplash.com/photo-1634084462412-b54873c0a56d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBzb2Z0d2FyZSUyMGRhc2hib2FyZCUyMFVJJTIwbW9ja3VwfGVufDB8fHx8MTc3ODUxNTg1Nnww&ixlib=rb-4.1.0&q=85",
        span: "md:col-span-7",
    },
    {
        title: "Logistics Dashboard",
        stack: ["Next.js", "Node.js", "MongoDB"],
        summary:
            "Real-time logistics command center for fleet, routes and delivery SLAs.",
        outcome: "21% drop in late deliveries",
        image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzb2Z0d2FyZSUyMGRhc2hib2FyZCUyMFVJJTIwbW9ja3VwfGVufDB8fHx8MTc3ODUxNTg1Nnww&ixlib=rb-4.1.0&q=85",
        span: "md:col-span-5",
    },
    {
        title: "Finance Analytics App",
        stack: ["React", "Python", "Recharts"],
        summary:
            "Self-serve analytics for CFO teams with drill-down financial reporting.",
        outcome: "10x faster board reports",
        image: "https://images.unsplash.com/photo-1634084462412-b54873c0a56d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBzb2Z0d2FyZSUyMGRhc2hib2FyZCUyMFVJJTIwbW9ja3VwfGVufDB8fHx8MTc3ODUxNTg1Nnww&ixlib=rb-4.1.0&q=85",
        span: "md:col-span-5",
    },
    {
        title: "Mobile Billing App",
        stack: ["Flutter", "FastAPI", "Postgres"],
        summary:
            "Offline-first billing app for field sales teams with thermal printing.",
        outcome: "Used by 1,200+ field reps",
        image: "https://images.unsplash.com/photo-1720135885007-454165745e21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBzb2Z0d2FyZSUyMGRhc2hib2FyZCUyMFVJJTIwbW9ja3VwfGVufDB8fHx8MTc3ODUxNTg1Nnww&ixlib=rb-4.1.0&q=85",
        span: "md:col-span-7",
    },
    {
        title: "Cloud Migration Platform",
        stack: ["AWS", "Terraform", "Kubernetes"],
        summary:
            "Lifted a monolithic finance system to a modular Kubernetes platform.",
        outcome: "44% lower infra cost",
        image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBzb2Z0d2FyZSUyMGRhc2hib2FyZCUyMFVJJTIwbW9ja3VwfGVufDB8fHx8MTc3ODUxNTg1Nnww&ixlib=rb-4.1.0&q=85",
        span: "md:col-span-4",
    },
    {
        title: "AI Business Assistant",
        stack: ["Python", "LLMs", "FastAPI"],
        summary:
            "An internal copilot that answers ops questions from company data.",
        outcome: "Hours saved every week",
        image: "https://images.unsplash.com/photo-1634084462412-b54873c0a56d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBzb2Z0d2FyZSUyMGRhc2hib2FyZCUyMFVJJTIwbW9ja3VwfGVufDB8fHx8MTc3ODUxNTg1Nnww&ixlib=rb-4.1.0&q=85",
        span: "md:col-span-4",
    },
    {
        title: "Restaurant Management System",
        stack: ["React Native", "Node.js", "Mongo"],
        summary:
            "Order, KOT, billing and inventory for multi-outlet restaurants.",
        outcome: "Live in 60+ outlets",
        image: "https://images.unsplash.com/photo-1720135885007-454165745e21?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBzb2Z0d2FyZSUyMGRhc2hib2FyZCUyMFVJJTIwbW9ja3VwfGVufDB8fHx8MTc3ODUxNTg1Nnww&ixlib=rb-4.1.0&q=85",
        span: "md:col-span-4",
    },
    {
        title: "ATS Resume Checker",
        stack: ["React", "Python", "NLP", "FastAPI"],
        summary:
            "AI-powered resume analyser that scores CVs against job descriptions and gives actionable ATS optimisation tips to job seekers.",
        outcome: "Helping thousands land more interviews",
        image: "https://images.unsplash.com/photo-1586281380349-632531db7ed4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NjUyNTV8MHwxfHNlYXJjaHwxfHxyZXN1bWUlMjBjYXJlZXIlMjBqb2IlMjBhcHBsaWNhdGlvbnxlbnwwfHx8fDE3NDcxNjAwMDB8MA&ixlib=rb-4.1.0&q=85",
        url: "https://www.atsresumeschecker.com",
        span: "md:col-span-12",
    },
];

export const TECH_STACK = {
    Frontend: ["React", "Next.js", "Flutter", "TypeScript"],
    Backend: ["Node.js", "Python", "FastAPI", "Express"],
    "Database & Cloud": [
        "PostgreSQL",
        "MongoDB",
        "AWS",
        "Azure",
        "Docker",
        "Kubernetes",
    ],
};

export const PROCESS = [
    {
        step: "01",
        title: "Discovery",
        desc: "We listen first — understanding your business, customers and constraints.",
    },
    {
        step: "02",
        title: "Planning",
        desc: "A clear roadmap with milestones, success metrics and engineering decisions.",
    },
    {
        step: "03",
        title: "UI/UX Design",
        desc: "Calm, modern interfaces shaped through prototypes and feedback loops.",
    },
    {
        step: "04",
        title: "Development",
        desc: "Iterative sprints with code reviews, CI and weekly product demos.",
    },
    {
        step: "05",
        title: "Testing",
        desc: "Automated and manual QA across devices, performance and security.",
    },
    {
        step: "06",
        title: "Deployment",
        desc: "Zero-downtime releases to cloud or on-prem environments.",
    },
    {
        step: "07",
        title: "Support & Scaling",
        desc: "Long-term partnership — observability, optimisations and new features.",
    },
];

export const TESTIMONIALS = [
    {
        quote: "Thooyra rebuilt our 12-year-old ERP without skipping a single workflow. The new platform is faster, cleaner and our finance team finally enjoys using it.",
        name: "Aravind Kumar",
        title: "CFO, Sundara Retail Group",
    },
    {
        quote: "From discovery to deployment, the team felt like an extension of ours. Their attention to UX is something we rarely see in enterprise software.",
        name: "Priya Narayanan",
        title: "Head of Operations, NorthFleet Logistics",
    },
    {
        quote: "We launched our SaaS MVP in 14 weeks. Customers consistently call out how polished the product feels — exactly the bar we wanted to hit.",
        name: "Karthik Raman",
        title: "Founder, Ledgerly",
    },
    {
        quote: "The mobile billing app changed how our field teams work. Offline reliability and the thoughtful UI were game-changers.",
        name: "Meera Iyer",
        title: "VP Sales, Coastal Distributors",
    },
];
